import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { notifyAccountSessionChanged } from '../../lib/accountSession';
import { SiteHead } from '../../components/site';

function ErrorBanner({ error }: { error: ApiError | null }) {
  if (!error) return null;

  return (
    <div className="banner" style={{ marginBottom: 20 }}>
      <span aria-hidden="true">!</span>
      <div>
        {error.message}
        {Object.keys(error.fields).length > 0 && (
          <ul style={{ margin: '8px 0 0 16px' }}>
            {Object.entries(error.fields).map(([field, reason]) => (
              <li key={field}>{field}: {Array.isArray(reason) ? reason[0] : reason}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function asApiError(cause: unknown): ApiError {
  return cause instanceof ApiError ? cause : new ApiError(0, 'UNKNOWN', '처리 실패', '-');
}

/** U001 — optional sign-in. An account is never required to play. */
export function AccountSignIn() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.account('/login', {
        method: 'POST',
        body: { email: form.get('email'), password: form.get('password') },
      });
      notifyAccountSessionChanged();
      navigate('/account');
    } catch (cause) {
      setError(asApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <SiteHead eyebrow="계정" title="로그인" lead="공지 읽기·기본 플레이는 로그인 없이 이용 가능" />
      <ErrorBanner error={error} />
      <form className="form-card" onSubmit={submit}>
        <label htmlFor="signin-email">이메일</label>
        <input id="signin-email" name="email" type="email" autoComplete="username" required />

        <label htmlFor="signin-password">비밀번호</label>
        <input id="signin-password" name="password" type="password" autoComplete="current-password" required />

        <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '확인 중…' : '로그인'}</button>

        <p className="form-foot">
          <Link to="/account/sign-up">회원가입</Link> · <Link to="/account/recovery">비밀번호 찾기</Link>
        </p>
      </form>
    </div>
  );
}

/** U002 — optional registration. Only what is needed is collected. */
export function AccountSignUp() {
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.account('/register', {
        method: 'POST',
        body: {
          email: form.get('email'),
          password: form.get('password'),
          age_eligibility_confirmed: form.get('age_eligibility_confirmed') === 'on',
          terms_accepted: form.get('terms_accepted') === 'on',
          marketing_opt_in: form.get('marketing_opt_in') === 'on',
        },
      });
      setDone(String(form.get('email')));
    } catch (cause) {
      setError(asApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="auth-page">
        <SiteHead eyebrow="계정" title="인증 메일 발송" lead={`${done} 주소로 코드 발송 완료`} />
        <div className="card">
          <Link className="btn btn--primary" to={`/account/verify?email=${encodeURIComponent(done)}`}>인증 코드 입력</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SiteHead eyebrow="계정" title="회원가입" lead="이메일·비밀번호로 가입" />
      <ErrorBanner error={error} />
      <form className="form-card" onSubmit={submit}>
        <label htmlFor="signup-email">이메일</label>
        <input id="signup-email" name="email" type="email" autoComplete="username" required />

        <label htmlFor="signup-password">비밀번호 (10자 이상)</label>
        <input id="signup-password" name="password" type="password" autoComplete="new-password" minLength={10} required />

        <label className="form-check">
          <input type="checkbox" name="age_eligibility_confirmed" required />
          <span>연령 기준 충족 (필수)</span>
        </label>
        <label className="form-check">
          <input type="checkbox" name="terms_accepted" required />
          <span><Link to="/legal/terms">이용약관</Link>·<Link to="/legal/privacy">개인정보 처리방침</Link> 동의 (필수)</span>
        </label>
        <label className="form-check">
          <input type="checkbox" name="marketing_opt_in" />
          <span>마케팅 정보 수신 동의 (선택)</span>
        </label>

        <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '처리 중…' : '가입'}</button>
        <p className="form-foot"><Link to="/account/sign-in">이미 계정이 있다면 로그인</Link></p>
      </form>
    </div>
  );
}

/** U003 — email verification, also used as the recovery code step. */
export function AccountVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const purpose = searchParams.get('purpose') === 'recovery' ? 'recovery' : 'email_verify';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [resent, setResent] = useState<number | null>(null);
  const [verified, setVerified] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.account('/verify-email', {
        method: 'POST',
        body: { email: form.get('email'), code: form.get('code'), purpose },
      });
      if (purpose === 'recovery') navigate('/account/recovery?step=set-password');
      else setVerified(true);
    } catch (cause) {
      setError(asApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function resend(email: string) {
    if (!email) return;
    const response = await api.account('/verify-email/resend', {
      method: 'POST',
      body: { email, purpose },
    }) as { data: { retry_after_seconds: number } };
    setResent(response.data.retry_after_seconds);
  }

  if (verified) {
    return (
      <div className="auth-page">
        <SiteHead eyebrow="계정" title="인증 완료" />
        <div className="card"><Link className="btn btn--primary" to="/account/sign-in">로그인</Link></div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SiteHead
        eyebrow="계정"
        title={purpose === 'recovery' ? '복구 코드 확인' : '이메일 인증'}
        lead="메일로 받은 코드 입력 · 유효 시간 확인"
      />
      <ErrorBanner error={error} />
      <form className="form-card" onSubmit={submit}>
        <label htmlFor="verify-email">이메일</label>
        <input id="verify-email" name="email" type="email" required defaultValue={searchParams.get('email') ?? ''} />

        <label htmlFor="verify-code">인증 코드</label>
        <input id="verify-code" name="code" inputMode="numeric" autoComplete="one-time-code" required />

        <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '확인 중…' : '확인'}</button>

        <p className="form-foot">
          <button
            type="button"
            className="link-button"
            onClick={(event) => {
              const form = event.currentTarget.closest('form');
              const email = form ? (new FormData(form).get('email') as string) : '';
              void resend(email);
            }}
          >
            코드 다시 받기
          </button>
          {resent !== null && <span> · {resent}초 뒤 다시 요청 가능</span>}
        </p>
      </form>
    </div>
  );
}

/** U004 — password recovery. Account existence is never disclosed. */
export function AccountRecovery() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const step = searchParams.get('step') === 'set-password' ? 'set-password' : 'request';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [requested, setRequested] = useState<string | null>(null);

  async function request(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.account('/recovery/request', { method: 'POST', body: { email: form.get('email') } });
      setRequested(String(form.get('email')));
    } catch (cause) {
      setError(asApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.account('/recovery/confirm', {
        method: 'POST',
        body: { password: form.get('password'), revoke_sessions: form.get('revoke_sessions') === 'on' },
      });
      navigate('/account/sign-in');
    } catch (cause) {
      setError(asApiError(cause));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'set-password') {
    return (
      <div className="auth-page">
        <SiteHead eyebrow="계정" title="새 비밀번호" lead="복구 코드 확인 완료" />
        <ErrorBanner error={error} />
        <form className="form-card" onSubmit={confirm}>
          <label htmlFor="new-password">새 비밀번호 (10자 이상)</label>
          <input id="new-password" name="password" type="password" autoComplete="new-password" minLength={10} required />
          <label className="form-check">
            <input type="checkbox" name="revoke_sessions" defaultChecked />
            <span>다른 기기의 로그인 전부 해제</span>
          </label>
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '변경 중…' : '비밀번호 변경'}</button>
        </form>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <SiteHead eyebrow="계정" title="비밀번호 찾기" lead="가입 이메일로 복구 코드 받기" />
      <ErrorBanner error={error} />
      {requested ? (
        <div className="card">
          <p style={{ margin: '0 0 16px', lineHeight: 1.8 }}>
            등록된 계정이면 복구 코드 발송 · 메일함 확인
          </p>
          <Link className="btn btn--primary" to={`/account/verify?purpose=recovery&email=${encodeURIComponent(requested)}`}>
            복구 코드 입력
          </Link>
        </div>
      ) : (
        <form className="form-card" onSubmit={request}>
          <label htmlFor="recovery-email">이메일</label>
          <input id="recovery-email" name="email" type="email" required />
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '요청 중…' : '복구 코드 받기'}</button>
          <p className="form-foot"><Link to="/account/sign-in">로그인으로 돌아가기</Link></p>
        </form>
      )}
    </div>
  );
}
